import fromEntries from 'fromentries';
import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Grid from '@material-ui/core/Grid';
import Card from '@material-ui/core/Card';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
} from '@material-ui/core';
import TablePagination from '@material-ui/core/TablePagination';
import CardHeader from '@material-ui/core/CardHeader';
import CardContent from '@material-ui/core/CardContent';
import Typography from '@material-ui/core/Typography';
import Tooltip from '@material-ui/core/Tooltip';
import withStyles from '@material-ui/core/styles/withStyles';
import { nsOptions } from '../../i18n';
import TitleCard from './TitleCard';
import VariableCard from './VariableCard';
import TextStats from './TextStats';
import { MEASUREMENTS, TITLE } from '../../constants';
import {
  formatChartData, getStatsElementTypeFromElement, formatTableDataHeaders, isHidden,
} from '../../utils/stats';
import FormattedValue from '../../utils/FormattedValue';
import DataOverviewModal from './DataOverviewModal';
import { CardLoader } from '../Loader';

const styles = (theme) => ({
  icon: {
    position: 'absolute',
    paddingTop: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(7.5),
    paddingBottom: theme.spacing(7.5),
    width: theme.spacing(12),
    height: theme.spacing(12),
    color: 'white',
    fontSize: theme.typography.pxToRem(theme.spacing(2.5)),
    background: `linear-gradient(to bottom right, ${theme.palette.primary.main} 0%, ${theme.palette.primary.main} 50%, rgba(0, 0, 0, 0) 50%)`,
  },
  cardContent: {
    paddingTop: 0,
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
  },
  statsContainer: {
    // Makes loader work
    position: 'relative',
    minHeight: '200px',
  },
  chartContainer: {
    height: '100%',
  },
  numberValue: {
    display: 'block',
    fontSize: theme.typography.pxToRem(75),
    lineHeight: 1,
    color: theme.palette.primary.main,
  },
  textContainer: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  dataOverviewGrid: {
    margin: 'auto',
    marginTop: '0.3rem',
  },
  tablePaginationSelect: {
    textAlign: 'left !important',
    textAlignLast: 'left !important',
    marginRight: theme.spacing(2),
  },
  tablePaginationSelectIcon: {
    right: -10,
  },
});


@withTranslation('', nsOptions)
@withStyles(styles)
class ModuleCard extends React.Component {
  static propTypes = {
    t: PropTypes.func.isRequired,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
    count: PropTypes.number.isRequired,
    children: PropTypes.arrayOf(PropTypes.any).isRequired,
    tableData: PropTypes.shape(),
    mean: PropTypes.number,
    textStats: PropTypes.shape().isRequired,
    loadData: PropTypes.func.isRequired,
    handleChildChange: PropTypes.func.isRequired,
    saveChildAxis: PropTypes.func.isRequired,
    statsLoading: PropTypes.bool,
    classes: PropTypes.shape().isRequired,
    project: PropTypes.shape().isRequired,
    canUseAdvancedTools: PropTypes.bool.isRequired,
    filtersParams: PropTypes.shape(),
    isLinkTarget: PropTypes.bool,
    elementType: PropTypes.string,
    decompositionMode: PropTypes.string,
    configChildAxis: PropTypes.shape(),
    variablePerPage: PropTypes.number,
  };

  static defaultProps = {
    mean: null,
    statsLoading: false,
    filtersParams: null,
    isLinkTarget: false,
    elementType: null,
    decompositionMode: 'none',
    configChildAxis: {},
    tableData: null,
    variablePerPage: 50,
  };

  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      currentPage: 1,
      pageSize: 10,
      variablePage: 1,
    };
    this.myRef = React.createRef();
  }

  showLoader = (show = true) => this.setState({ loading: show });

  handlePageChange = (event, newPage) => {
    this.setState({ currentPage: newPage + 1 });
  };

  handleRowsPerPageChange = (event) => {
    const newPageSize = parseInt(event.target.value, 10);
    this.setState({ pageSize: newPageSize, currentPage: 1 });
  };

  renderCenterVariableTable = () => {
    const {
      t, classes, tableData,
    } = this.props;
    const { loading, currentPage, pageSize } = this.state;
    const offset = (currentPage - 1) * pageSize;

    if (loading) {
      return (
        <div className={classes.empty}>
          <CardLoader />
        </div>
      );
    }

    if (!tableData || tableData.length === 0) {
      console.log('No table data');
      return <div>{t('stats:no-table-data')}</div>;
    }

    const { headers, rows } = tableData;
    if (!Array.isArray(headers) || !Array.isArray(rows)) {
      console.log('No table data');
      return <div>{t('stats:no-table-data')}</div>;
    }

    return (
      <div>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow style={{ backgroundColor: '#f5f5f5' }}>
                <TableCell />
                {formatTableDataHeaders(headers).map((col) => (
                  <TableCell key={col} style={{ fontWeight: 'bold' }}>
                    {col}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, rowIndex) => {
                if (
                  rowIndex < offset
                  || rowIndex >= offset + pageSize
                ) {
                  return null;
                }

                return (
                // eslint-disable-next-line react/no-array-index-key
                  <TableRow key={`row-${row[0]}-${rowIndex}`}>
                    {row.map((cell, cellIndex) => (
                      <TableCell
                        // eslint-disable-next-line react/no-array-index-key
                        key={`cell-${cell}-${cellIndex}`}
                        style={{
                          // eslint-disable-next-line no-nested-ternary
                          fontWeight: cellIndex === 0 ? 'bold'
                            : cell === 'Total' ? 'bold' : 'normal',
                        }}
                      >
                        {cell}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          rowsPerPageOptions={[5, 10, 25, 50]}
          count={rows.length}
          rowsPerPage={this.state.pageSize}
          page={this.state.currentPage - 1}
          onChangePage={this.handlePageChange}
          onChangeRowsPerPage={this.handleRowsPerPageChange}
          SelectProps={{
            classes: {
              select: classes.tablePaginationSelect,
              icon: classes.tablePaginationSelectIcon,
            },
          }}
          labelRowsPerPage={t('common:table.rows-per-page')}
          // eslint-disable-next-line object-curly-newline
          labelDisplayedRows={({ from, to, count, page }) => {
            const total = count !== -1 ? count : -page;
            return `${from}-${to} ${t('common:table.of')} ${total}`;
          }}
        />
      </div>
    );
  };

  scrollToRef = () => {
    this.myRef.current.scrollIntoView({
      behavior: 'smooth',
    });
  }

  renderButtonChangeVariablePage = (nextPage) => {
    const { variablePage } = this.state;
    if (!nextPage && variablePage === 1) {
      return null;
    }
    const { t, children, variablePerPage } = this.props;
    if (nextPage && (variablePage * variablePerPage) >= children.length) {
      return null;
    }

    return (
      <button
        type="button"
        className="btn page-link mt-4"
        onClick={() => {
          this.setState({ variablePage: variablePage + (nextPage ? 1 : -1) });
          // this.scrollToRef();
        }}
      >
        {nextPage ? t('stats:display-next-variables') : t('stats:display-previous-variables')}
      </button>
    );
  };

  render() {
    const {
      id, name, count, children, t, classes, mean, textStats, loadData, statsLoading,
      project, canUseAdvancedTools, filtersParams, isLinkTarget, elementType, decompositionMode,
      handleChildChange, saveChildAxis, configChildAxis, variablePerPage,
    } = this.props;
    const { loading, variablePage } = this.state;
    return (
      <Card>
        <Tooltip title={t('stats:module')} placement="top" ref={this.myRef}>
          <div className={classes.icon}>
            <FontAwesomeIcon icon={['far', 'sitemap']} />
          </div>
        </Tooltip>
        <CardHeader
          title={name}
          titleTypographyProps={{ align: 'center', variant: 'h6' }}
        />
        <CardContent className={classes.cardContent}>
          <Grid
            container
            alignItems="center"
            spacing={4}
            className={classes.statsContainer}
          >
            {loading && <CardLoader />}
            <Grid item xs={12} md={6}>
              <Grid
                container
                direction="row"
                className={classes.chartContainer}
              >
                <Grid item xs={12}>
                  <Typography align="center">
                    <span className={classes.numberValue}>
                      <FormattedValue value={mean} />
                    </span>
                    {t('stats:entries-per-inclusion')}
                  </Typography>
                </Grid>
              </Grid>
              {
                !count && (
                  <Grid
                    container
                    direction="row"
                    className={classes.chartContainer}
                  >
                    <Grid item xs={8} className={classes.dataOverviewGrid}>
                      <DataOverviewModal
                        id={id}
                        name={name}
                        isModule
                        count={count}
                        filtersParams={filtersParams}
                        project={project}
                        isLinkTarget={isLinkTarget}
                        elementType={elementType}
                        showLoader={this.showLoader}
                        canUseAdvancedTools={canUseAdvancedTools}
                      />
                    </Grid>
                  </Grid>
                )
              }
            </Grid>
            <Grid item xs={12} md={6}>
              <Grid
                container
                direction="row"
                className={classes.textContainer}
              >
                <Grid item xs={12}>
                  <TextStats
                    id={id}
                    type={MEASUREMENTS}
                    unit={t('stats:entries-per-inclusion')}
                    count={count}
                    variableName={name}
                    filtersParams={filtersParams}
                    isModule
                    isLinkTarget={isLinkTarget}
                    project={project}
                    elementType={elementType}
                    showLoader={this.showLoader}
                    canUseAdvancedTools={canUseAdvancedTools}
                    {...textStats}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          {
            decompositionMode !== null && (
              <Grid item xs={12} className={classes.chartSubContainer}>
                {this.renderCenterVariableTable()}
              </Grid>
            )
          }
          <Grid container direction="column" spacing={4}>
            {
              count ? children.filter(
                // eslint-disable-next-line max-len
                (elementStats, index) => !isHidden(elementStats) && index < variablePage * variablePerPage,
              ).map((child) => {
                const type = getStatsElementTypeFromElement(child);
                let content;
                if (type === TITLE) {
                  content = <TitleCard name={child.name} level={child.level} />;
                } else if (type === null) {
                  content = null;
                } else {
                  content = (
                    <VariableCard
                      inModule
                      moduleId={id}
                      moduleName={name}
                      {...{
                        id: child.id,
                        name: child.name,
                        isLinkTarget: child.is_link_target,
                        type,
                        elementType: child.type,
                        unit: child.unit,
                        seriesNames: child.iterations.map((iteration) => iteration.name),
                        seriesRawNames: child.iterations.map((iteration) => iteration.raw_name),
                        count: child.count,
                        seriesNamesCounts: fromEntries(
                          child.iterations.map(
                            (iteration) => [iteration.name, iteration.count],
                          ),
                        ),
                        iterations: child.iterations,
                        chartData: formatChartData(child),
                        tableData: child.table_data,
                        textStats: {
                          count: child.count,
                          min: child.min,
                          max: child.max,
                          median: child.median,
                          mean: child.mean,
                          variance: child.variance,
                          stddev: child.stddev,
                          interquartileRange: child.interquartile_range,
                        },
                      }}
                      loadData={loadData}
                      statsLoading={statsLoading}
                      project={project}
                      canUseAdvancedTools={canUseAdvancedTools}
                      filtersParams={filtersParams}
                      decompositionMode={decompositionMode}
                      handleChildChange={handleChildChange}
                      saveChildAxis={saveChildAxis}
                      configAxis={configChildAxis[child.id]}
                    />
                  );
                }
                return <Grid item xs={12} key={child.id}>{content}</Grid>;
              }) : null
            }
            {this.renderButtonChangeVariablePage(true)}
          </Grid>
        </CardContent>
      </Card>
    );
  }
}

export default ModuleCard;
